<template>
  <v-dialog v-model="internalValue" max-width="400" persistent @click:outside="close">
    <v-card class="bookmark-dialog overflow-hidden rounded-xl border border-white-05">
      <div class="dialog-header pa-4 d-flex align-center bg-surface-variant">
        <v-icon icon="mdi-bookmark-plus" class="mr-3 text-primary"></v-icon>
        <div class="text-h6 font-weight-bold">Save to Bookmarks</div>
        <v-spacer></v-spacer>
        <v-btn icon="mdi-close" variant="text" size="small" @click="close"></v-btn>
      </div>

      <v-card-text class="pa-4">
        <div v-if="loading" class="text-center py-6">
          <v-progress-circular indeterminate color="primary"></v-progress-circular>
          <div class="mt-2 text-caption text-grey">Loading your groups...</div>
        </div>

        <div v-else>
          <div v-if="groups.length > 0" class="group-list mb-4">
            <v-checkbox
              v-for="group in groups"
              :key="group.id"
              v-model="selectedGroupIds"
              :label="group.name"
              :value="group.id"
              density="compact"
              hide-details
              color="primary"
              class="mb-1 rounded-lg px-2 hover-bg"
            >
              <template v-slot:label>
                <div class="d-flex align-center w-100">
                  <span class="font-weight-medium text-body-2">{{ group.name }}</span>
                  <v-spacer></v-spacer>
                  &nbsp
                  &#40;
                  <span class="text-caption text-grey">{{ group.postUris.length }} posts</span>
                  &#41;
                </div>
              </template>
            </v-checkbox>
          </div>

          <div v-else class="text-center py-6 text-grey text-body-2">
            No bookmark groups yet.
          </div>

          <v-divider class="my-4"></v-divider>

          <div v-if="!showNewInput">
            <v-btn
              prepend-icon="mdi-plus"
              variant="text"
              block
              class="text-none justify-start px-2"
              @click="showNewInput = true"
            >
              Create New Group
            </v-btn>
          </div>
          <div v-else class="new-group-input d-flex align-center">
            <v-text-field
              v-model="newGroupName"
              placeholder="Group name"
              density="compact"
              variant="outlined"
              hide-details
              class="rounded-lg mr-2"
              autofocus
              @keyup.enter="createGroup"
            ></v-text-field>
            <v-btn icon="mdi-check" color="success" variant="tonal" size="small" @click="createGroup"></v-btn>
            <v-btn icon="mdi-close" variant="text" size="small" @click="showNewInput = false; newGroupName = ''"></v-btn>
          </div>
        </div>
      </v-card-text>

      <v-divider></v-divider>

      <v-card-actions class="pa-4 bg-black-05">
        <v-spacer></v-spacer>
        <v-btn variant="text" @click="close">Cancel</v-btn>
        <v-btn color="primary" variant="flat" :loading="saving" @click="save" class="rounded-lg px-6">
          Save
        </v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script setup lang="ts">
import { ref, watch, computed } from 'vue';
import { getBookmarks, saveBookmarks, showAlert } from '@/services/bluesky';

const props = defineProps({
  modelValue: Boolean,
  postUri: String
});

const emit = defineEmits(['update:modelValue', 'saved']);

const internalValue = computed({
  get: () => props.modelValue,
  set: (val) => emit('update:modelValue', val)
});

const loading = ref(true);
const saving = ref(false);
const groups = ref<any[]>([]);
const selectedGroupIds = ref<string[]>([]);
const showNewInput = ref(false);
const newGroupName = ref('');

const fetchBookmarks = async () => {
  loading.value = true;
  const bookmarks = await getBookmarks();
  if (bookmarks) {
    groups.value = bookmarks.groups || [];
    // Pre-select groups containing this post
    selectedGroupIds.value = groups.value
      .filter(g => g.postUris.includes(props.postUri))
      .map(g => g.id);
  }
  loading.value = false;
};

const createGroup = () => {
  if (!newGroupName.value.trim()) return;
  const newId = Math.random().toString(36).substring(2, 11);
  groups.value.push({
    id: newId,
    name: newGroupName.value.trim(),
    postUris: []
  });
  selectedGroupIds.value.push(newId);
  newGroupName.value = '';
  showNewInput.value = false;
};

const save = async () => {
  saving.value = true;
  try {
    // Update every group's postUris based on selectedGroupIds
    const updatedGroups = groups.value.map(group => {
      const isSelected = selectedGroupIds.value.includes(group.id);
      const hasPost = group.postUris.includes(props.postUri);

      if (isSelected && !hasPost) {
        return { ...group, postUris: [...group.postUris, props.postUri] };
      } else if (!isSelected && hasPost) {
        return { ...group, postUris: group.postUris.filter((uri: string) => uri !== props.postUri) };
      }
      return group;
    });

    await saveBookmarks({ groups: updatedGroups });
    emit('saved');
    close();
  } catch (e) {
    await showAlert('Failed to save bookmarks');
  } finally {
    saving.value = false;
  }
};

const close = () => {
  internalValue.value = false;
};

watch(() => props.modelValue, (newVal) => {
  if (newVal) {
    fetchBookmarks();
  } else {
    showNewInput.value = false;
    newGroupName.value = '';
  }
});
</script>

<style scoped>
.bookmark-dialog {
  background: rgba(30, 30, 30, 0.95);
  backdrop-filter: blur(20px);
}
.dialog-header {
  background: rgba(255, 255, 255, 0.05);
}
.hover-bg:hover {
  background: rgba(255, 255, 255, 0.05);
}
.bg-black-05 {
  background: rgba(0, 0, 0, 0.2);
}
.border-white-05 {
  border-color: rgba(255, 255, 255, 0.05) !important;
}
</style>
